import React, { useEffect, useState } from 'react';
import { DayTimeSlot, InterviewPathType, SlotTime } from 'types';
import { DateSelection } from './DateSelection';
import { TimeSelection } from './TimeSelection';
import { BsArrowLeft } from 'react-icons/bs';
import { Button, Spinner } from 'flowbite-react';
import { AgreedCheckbox } from './AgreedCheckbox';
import { useUserContext } from 'src/context/UserProvider';
import moment from 'moment';
import { sendTrackingEvent } from 'src/utils/gtm';
import { useLocation } from 'react-router-dom';
import SchedulePaidRoundDrawer from '../dashboard/screeningRound/SchedulePaidRoundDrawer';
import { findRemainingAttempts } from 'shared-utils';
import AIInterviewScheduleStep from './AIInterviewScheduleStep';

interface Props {
	allSlots: DayTimeSlot[];
	selectedTeckStack: string;
	currentStep: number;
	selectedDaySlot?: DayTimeSlot;
	selectedTimeSlot?: SlotTime;
	setSelectedDaySlot: React.Dispatch<React.SetStateAction<DayTimeSlot | undefined>>;
	setSelectedTimeSlot: React.Dispatch<React.SetStateAction<SlotTime | undefined>>;
	setCurrentStep: (val: number) => void;
	onSchedule: () => void;
	isLoading?: boolean;
	onClose: () => void;
	handleScheduleInterviewForPaidUser: () => void;
	hasAlreadyPaidForInterview: boolean;
	isAIRoundSelected: boolean;
}

export const ScheduleMeetingStep = ({
	allSlots,
	onClose,
	selectedTeckStack,
	setCurrentStep,
	selectedDaySlot,
	selectedTimeSlot,
	setSelectedDaySlot,
	setSelectedTimeSlot,
	isLoading,
	onSchedule,
	handleScheduleInterviewForPaidUser,
	hasAlreadyPaidForInterview,
	isAIRoundSelected,
}: Props) => {
	const location = useLocation();
	const { hasReschedulableMeeting, user, userRounds } = useUserContext();
	const [hasAgreed, setHasAgreed] = useState(false);
	const [isSchedulePaidRoundDrawerOpen, setIsSchedulePaidRoundDrawerOpen] = useState(false);
	const [availableRounds, setAvailableRounds] = useState(userRounds);

	const isPaidUser = user?.evaluation?.interviewPath?.type === InterviewPathType.Paid;

	const shouldShowPaymentOption = isPaidUser && !hasAlreadyPaidForInterview && availableRounds.length === 0;

	useEffect(() => {
		setAvailableRounds(userRounds.filter(r => findRemainingAttempts(r.maxAttempts, r.meetings)));
	}, [userRounds]);

	return (
		<>
			<div className="px-[12px] py-[16px] font-inter flex flex-col gap-4">
				{isAIRoundSelected ? (
					<AIInterviewScheduleStep
						selectedTimeSlot={selectedTimeSlot ?? { time: new Date(), count: 1 }}
						setSelectedTimeSlot={setSelectedTimeSlot}
					/>
				) : (
					<div>
						<DateSelection
							allSlots={allSlots}
							selectedDaySlot={selectedDaySlot}
							setSelectedDaySlot={(value: DayTimeSlot) => {
								setSelectedDaySlot(value);
								setSelectedTimeSlot(undefined);
							}}
						/>
						<TimeSelection
							selectedDaySlot={selectedDaySlot}
							selectedTimeSlot={selectedTimeSlot}
							setSelectedTimeSlot={setSelectedTimeSlot}
						/>
					</div>
				)}

				<AgreedCheckbox agreed={hasAgreed} setHasAgreed={setHasAgreed} styleing="text-[12px]" />
				<div className="flex flex-row gap-3 items-center">
					{!hasReschedulableMeeting && (
						<Button
							color="purplePlaced"
							onClick={() => setCurrentStep(1)}
							className="border py-1 border-[#5B8DEF] bg-transparent hover:text-white text-[#7777E7] dark:text-white"
						>
							<BsArrowLeft />
						</Button>
					)}

					<Button
						color="purplePlaced"
						className="bg-[#7777E7] w-full text-white"
						disabled={(!isAIRoundSelected && !selectedDaySlot) || !selectedTimeSlot || isLoading || !hasAgreed}
						onClick={() => {
							if (hasReschedulableMeeting) {
								sendTrackingEvent({
									event: 'interview_reschedule',
									eventLabel: location.pathname,
								});
							} else {
								sendTrackingEvent({
									event: 'interview_schedule',
									eventLabel: location.pathname,
								});
							}
							if (shouldShowPaymentOption) {
								setIsSchedulePaidRoundDrawerOpen(true);
							} else {
								onSchedule();
							}
						}}
					>
						{isLoading ? (
							<Spinner className="mr-3" />
						) : (
							`${hasReschedulableMeeting ? 'Reschedule' : 'Schedule'} for ${moment(selectedTimeSlot?.time).format(
								'DD MMM',
							)} at ${moment(selectedTimeSlot?.time).format('hh:mm a')}`
						)}
					</Button>
				</div>
			</div>
			<SchedulePaidRoundDrawer
				isOpen={isSchedulePaidRoundDrawerOpen}
				onClose={() => setIsSchedulePaidRoundDrawerOpen(false)}
				handleScheduling={onSchedule}
				hasAgreed={hasAgreed}
			/>
		</>
	);
};
